import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { hideGlobalDialogErrorContext } from '@aionzdev/modules';
import { inject, Injectable } from '@angular/core';
import { catchError, map, Observable, of, throwError } from 'rxjs';

import { environment } from '../../../../environments/environment';
import {
  ProductPurchaseEntry,
  ProductPurchaseHistoryPage,
  PurchaseHistoryDialogData
} from '../interfaces/product-purchase-history';

/** Resposta proposta ao backend (ver `ideias/historico-compras-produto.md`). */
interface HistoricoComprasProdutoDto {
  resumo: {
    menorPreco: number | null;
    maiorPreco: number | null;
    precoMedio: number | null;
    quantidadeMedia: number | null;
    total: number;
  };
  compras: {
    data: string;
    fornecedorNome: string;
    quantidade: number;
    unidadeMedida: string | null;
    precoUnitario: number;
    origem: 'cotai' | 'erp';
    ofertaEstruturadaId: number | null;
  }[];
}

/**
 * Histórico completo de compras do produto. A rota ainda não existe no ebarn-node: no
 * localhost, enquanto ela responder 404, o modal mostra dados de exemplo avisando isso.
 */
@Injectable({ providedIn: 'root' })
export class ProductPurchaseHistoryService {
  private readonly http = inject(HttpClient);
  private readonly baseUrl = `${environment.apiUrl}/oferta-estruturada`;

  load(
    request: PurchaseHistoryDialogData,
    page: number,
    pageSize: number
  ): Observable<ProductPurchaseHistoryPage> {
    return this.http
      .get<HistoricoComprasProdutoDto>(
        `${this.baseUrl}/${request.purchaseId}/produtos/${request.productId}/historico-compras`,
        // o modal mostra o próprio erro (e, no localhost, os dados de exemplo): sem o diálogo global
        { params: { page, pageSize }, context: hideGlobalDialogErrorContext }
      )
      .pipe(
        map(dto => this.toPage(dto, page, pageSize, request.unitLabel)),
        catchError((error: unknown) => {
          const rotaAusente = error instanceof HttpErrorResponse && error.status === 404;
          if (rotaAusente && isLocalhost()) return of(demoPage(request, page, pageSize));
          return throwError(() => error);
        })
      );
  }

  private toPage(
    dto: HistoricoComprasProdutoDto,
    page: number,
    pageSize: number,
    unitLabel: string
  ): ProductPurchaseHistoryPage {
    return {
      summary: {
        lowestPrice: dto.resumo?.menorPreco ?? null,
        highestPrice: dto.resumo?.maiorPreco ?? null,
        averagePrice: dto.resumo?.precoMedio ?? null,
        averageQuantity: dto.resumo?.quantidadeMedia ?? null,
        count: dto.resumo?.total ?? 0
      },
      entries: (dto.compras ?? []).map(compra => ({
        date: compra.data,
        supplierName: compra.fornecedorNome,
        quantity: Number(compra.quantidade),
        unit: compra.unidadeMedida || unitLabel,
        unitPrice: Number(compra.precoUnitario),
        origin: compra.origem,
        purchaseRequestId: compra.ofertaEstruturadaId != null ? String(compra.ofertaEstruturadaId) : null
      })),
      page,
      pageSize
    };
  }
}

function isLocalhost(): boolean {
  const host = globalThis.location?.hostname ?? '';
  return host === 'localhost' || host === '127.0.0.1';
}

/**
 * Exemplo plausível a partir do que o mapa já sabe (último preço e fornecedores): 14 compras
 * mensais com o preço variando em torno do último pago. Sempre igual para o mesmo produto.
 */
function demoPage(
  request: PurchaseHistoryDialogData,
  page: number,
  pageSize: number
): ProductPurchaseHistoryPage {
  const base = request.lastPurchasePrice && request.lastPurchasePrice > 0 ? request.lastPurchasePrice : 100;
  const suppliers = request.supplierNames.length ? request.supplierNames : ['Fornecedor A', 'Fornecedor B'];
  const lastDate = request.lastPurchaseDate ? new Date(request.lastPurchaseDate) : new Date();
  const seed = [...request.productId].reduce((acc, ch) => acc + ch.charCodeAt(0), 0);
  const variacoes = [0, 0.04, -0.03, 0.08, 0.02, -0.05, 0.12, 0.06, -0.02, 0.15, 0.1, 0.03, 0.18, 0.09];

  const all: ProductPurchaseEntry[] = variacoes.map((variacao, index) => {
    const date = new Date(lastDate.getFullYear(), lastDate.getMonth() - index, Math.min(lastDate.getDate(), 28));
    return {
      date: date.toISOString(),
      supplierName: suppliers[(seed + index) % suppliers.length],
      quantity: 400 + ((seed + index * 137) % 7) * 100,
      unit: request.unitLabel,
      unitPrice: Math.round(base * (1 + variacao) * 100) / 100,
      origin: index % 4 === 3 ? 'erp' : 'cotai',
      purchaseRequestId: index % 4 === 3 ? null : String(1000 - index * 7)
    };
  });

  const prices = all.map(entry => entry.unitPrice);
  const quantities = all.map(entry => entry.quantity);
  const average = (values: number[]) => values.reduce((sum, value) => sum + value, 0) / values.length;
  const start = (page - 1) * pageSize;

  return {
    summary: {
      lowestPrice: Math.min(...prices),
      highestPrice: Math.max(...prices),
      averagePrice: average(prices),
      averageQuantity: average(quantities),
      count: all.length
    },
    entries: all.slice(start, start + pageSize),
    page,
    pageSize,
    demo: true
  };
}
